/**
 * 母数のセレクタ。
 *
 * **選べるものは `Query.populations` が決める。** API が honor できない母集団は
 * 返ってこないので、画面は返ってきたものをそのまま並べるだけでよい。
 * 「並べておいて、選んだら BAD_USER_INPUT」という形にはしない。
 *
 * 数字の欄（`products` / `answerRate`）は集計が届いていなければ null で返る。
 * 0 と描くと「数えたら 0 件だった」と読めるので、そのときは何も出さない。
 */

import { useQuery } from "urql";

import { HelpIcon } from "../help/HelpIcon";
import {
  PopulationsDocument,
  type PopulationId,
} from "visumo-data-agent-web-shared";

export function PopulationSelect({
  value,
  onChange,
  label = "母数",
}: {
  value: PopulationId;
  onChange: (next: PopulationId) => void;
  label?: string;
}) {
  const [result] = useQuery({ query: PopulationsDocument });
  const populations = result.data?.populations ?? [];

  if (result.fetching && result.data === undefined) {
    return (
      <p className="vd-stat-note" style={{ margin: 0 }}>
        選べる母数を読み込んでいます。
      </p>
    );
  }
  if (populations.length === 0) {
    return (
      <p className="vd-stat-note" style={{ margin: 0 }}>
        いま選べる母数がありません。母集団を区別できる記録がまだ届いていません。
      </p>
    );
  }

  const selected = populations.find((item) => item.id === value) ?? null;

  return (
    <div>
      <span className="vd-row" style={{ alignItems: "flex-end" }}>
        <label className="vd-field">
          <span className="vd-field-hint">{label}</span>
          <select
            className="vd-select"
            value={value}
            onChange={(event) => onChange(event.target.value as PopulationId)}
          >
            {populations.map((population) => (
              <option key={population.id} value={population.id}>
                {population.label}
              </option>
            ))}
          </select>
        </label>
        <HelpIcon slug="getting-started/population" />
      </span>
      {selected === null ? null : (
        <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
          {selected.note}
          {selected.products === null
            ? ""
            : ` いまこの母数に入っているのは ${selected.products.toLocaleString("ja-JP")} 件です。`}
        </p>
      )}
    </div>
  );
}
